import { cachedFetch } from "theme";

const settings = document.querySelector("script[data-quick-buy-colors]").dataset;
const states = new WeakMap();
const colorSelector = 'quick-buy-modal [data-block-type="product-variations"] a[href]';
const optionSelector = 'input[type="radio"][data-option-position]';
const renders = new Map();
const renderLifetime = 30000;
const renderLimit = 8;

function productKey(url) {
  return url.origin + url.pathname.replace(/\/+$/, "");
}

function stabilizeColors(root) {
  root.querySelectorAll('[data-block-type="product-variations"] .variant-picker__option-values').forEach((list) => {
    const links = Array.from(list.children).filter((element) => element.matches("a[href]"));
    const ordered = links.map((link) => ({ link, key: productKey(new URL(link.href, location.href)) }))
      .sort((a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0).map((item) => item.link);
    if (ordered.some((link, index) => link !== links[index])) list.append(...ordered);
  });
}

function liveRenders() {
  const now = Date.now();
  for (const [key, entry] of renders) {
    if (now - entry.at >= renderLifetime) renders.delete(key);
  }
  return renders;
}

function remember(key, url, fragment) {
  renders.delete(key);
  renders.set(key, { at: Date.now(), product: productKey(url), fragment: fragment.cloneNode(true) });
  if (renders.size > renderLimit) renders.delete(renders.keys().next().value);
}

function groups(root) {
  return Array.from(root.querySelectorAll("variant-picker fieldset")).map((fieldset) => ({
    name: fieldset.querySelector("legend")?.textContent.trim(),
    inputs: Array.from(fieldset.querySelectorAll(optionSelector))
  })).filter((group) => group.inputs.length);
}

function choices(root) {
  return groups(root).map((group) => ({
    name: group.name,
    label: group.inputs.find((input) => input.checked)?.dataset.optionLabel
  }));
}

function matchingOptions(root, previous) {
  return groups(root).map((group) => {
    const selected = previous.find((choice) => choice.name === group.name);
    return selected?.label == null ? null : group.inputs.find((input) => input.dataset.optionLabel === selected.label);
  });
}

function message(modal, text) {
  modal.querySelector("[data-quick-buy-status]")?.remove();
  const status = document.createElement("p");
  status.dataset.quickBuyStatus = "";
  status.className = "text-subdued";
  status.setAttribute("role", "status");
  status.textContent = text;
  modal.querySelector(".quick-buy-modal__info-wrapper").prepend(status);
}

function lock(modal, locked) {
  modal.querySelectorAll("buy-buttons").forEach((buttons) => { buttons.inert = locked; });
}

function loading(modal, link) {
  message(modal, link.querySelector("img")?.alt || settings.chooseOptions);
  const progress = document.createElement("progress");
  progress.setAttribute("aria-label", settings.chooseOptions);
  progress.style.width = "100%";
  progress.style.height = "4px";
  progress.style.accentColor = "currentColor";
  modal.querySelector("[data-quick-buy-status]").append(progress);
}

async function requestContent(url, signal) {
  const response = await cachedFetch(url.href, { signal });
  if (!response.ok) throw new Error("Quick buy product request failed");
  const template = new DOMParser().parseFromString(await response.text(), "text/html").getElementById("quick-buy-content");
  if (!template?.content.querySelector("product-form form")) throw new Error("Quick buy content missing");
  const fragment = template.content;
  fragment.querySelectorAll("noscript").forEach((element) => element.remove());
  return fragment;
}

async function content(url, signal, section) {
  if (section) {
    const sectionUrl = new URL(url.href);
    sectionUrl.searchParams.set("section_id", section);
    try {
      return await requestContent(sectionUrl, signal);
    } catch (error) {
      if (signal.aborted) throw error;
    }
  }
  return requestContent(url, signal);
}

function install() {
  const installed = Symbol.for("blackbison.quick-buy-colors");
  if (window[installed]) return;
  window[installed] = true;

  document.addEventListener("click", async (event) => {
    const link = event.target.closest?.(colorSelector);
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const modal = link.closest("quick-buy-modal");
    if (!modal.open) return;
    event.preventDefault();
    if (link.classList.contains("is-selected") && !states.get(modal)?.loading) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || !url.pathname.includes("/products/")) return;
    const previousState = states.get(modal);
    if (previousState?.loading && previousState.target === url.href) return;
    previousState?.controller.abort();
    const state = {
      controller: new AbortController(),
      loading: true,
      target: url.href,
      awaiting: previousState?.awaiting || false,
      originalLabel: previousState ? previousState.originalLabel : modal.getAttribute("aria-label")
    };
    states.set(modal, state);
    const previous = choices(modal);
    const quantity = modal.querySelector('[name="quantity"]')?.value;
    modal.setAttribute("aria-busy", "true");
    lock(modal, true);
    modal.querySelectorAll("variant-picker, quantity-selector").forEach((element) => { element.inert = true; });
    try {
      loading(modal, link);
      const key = productKey(url) + JSON.stringify(previous);
      const cached = liveRenders().get(key);
      const known = Array.from(renders.values()).find((entry) => entry.product === productKey(url));
      let section;
      if (!cached && known) {
        const options = matchingOptions(known.fragment, previous);
        if (options.length && options.every(Boolean)) {
          url.searchParams.delete("variant");
          url.searchParams.set("option_values", options.map((input) => input.value).join(","));
          section = known.fragment.querySelector("variant-picker")?.getAttribute("section-id");
        }
      }
      let fragment = cached ? cached.fragment.cloneNode(true) : await content(url, state.controller.signal, section);
      if (state.controller.signal.aborted || states.get(modal) !== state || !modal.open) return;
      const matches = matchingOptions(fragment, previous);
      let reselect = matches.some((input) => !input || input.dataset.optionAvailable === "false");
      if (!reselect && matches.some((input) => !input.checked)) {
        url.searchParams.delete("variant");
        url.searchParams.set("option_values", matches.map((input) => input.value).join(","));
        const targetSection = fragment.querySelector("variant-picker")?.getAttribute("section-id");
        fragment = await content(url, state.controller.signal, targetSection);
        reselect = matchingOptions(fragment, previous).some((input) => !input?.checked);
      }
      if (state.controller.signal.aborted || states.get(modal) !== state || !modal.isConnected || !modal.open) return;
      if (!cached) remember(key, url, fragment);
      stabilizeColors(fragment);
      if (reselect) {
        fragment.querySelectorAll(optionSelector).forEach((input) => { input.checked = false; input.required = true; });
        fragment.querySelectorAll('product-form [name="id"]').forEach((input) => { input.value = ""; });
        fragment.querySelectorAll('buy-buttons [type="submit"]').forEach((button) => {
          button.disabled = true;
          button.textContent = settings.chooseOptions;
        });
      }
      modal.replaceChildren(fragment);
      const title = modal.querySelector(".product-title")?.textContent.trim();
      if (title) modal.setAttribute("aria-label", settings.chooseOptions + ": " + title);
      const input = modal.querySelector('[name="quantity"]');
      if (input && quantity) {
        const minimum = Number(input.min) || 1;
        const maximum = input.max ? Number(input.max) : Infinity;
        const step = Number(input.step) || 1;
        const value = Math.max(minimum, minimum + Math.floor((Math.min(Number(quantity), maximum) - minimum) / step) * step);
        const selector = input.closest("quantity-selector");
        if (selector) selector.quantity = value;
        else input.value = String(value);
      }
      state.awaiting = reselect;
      if (reselect) message(modal, settings.chooseOptions);
      lock(modal, reselect);
      window.Shopify?.PaymentButton?.init();
      modal.querySelector('[data-block-type="product-variations"] a.is-selected')?.focus({ preventScroll: true });
    } catch (error) {
      if (!state.controller.signal.aborted && states.get(modal) === state && modal.open) message(modal, settings.loadError);
    } finally {
      if (states.get(modal) === state) {
        state.loading = false;
        modal.removeAttribute("aria-busy");
        lock(modal, state.awaiting);
        modal.querySelectorAll("variant-picker, quantity-selector").forEach((element) => { element.inert = false; });
      }
    }
  });

  document.addEventListener("dialog:before-show", (event) => {
    if (event.target.matches("quick-buy-modal")) stabilizeColors(event.target);
  }, true);

  document.addEventListener("product:rerender", (event) => {
    if (!event.target.closest?.("quick-buy-modal")) return;
    const fragment = event.detail?.htmlFragment;
    const root = fragment?.querySelector("#quick-buy-content")?.content || fragment;
    if (root) stabilizeColors(root);
  }, true);

  document.addEventListener("change", (event) => {
    const modal = event.target.closest?.("quick-buy-modal");
    const state = modal && states.get(modal);
    if (!state?.awaiting || !event.target.matches(optionSelector)) return;
    if (groups(modal).some((group) => !group.inputs.some((input) => input.checked))) {
      event.stopImmediatePropagation();
    } else {
      state.awaiting = false;
    }
  }, true);

  document.addEventListener("submit", (event) => {
    const modal = event.target.closest("quick-buy-modal");
    const state = modal && states.get(modal);
    if (state?.loading || state?.awaiting) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener("dialog:before-hide", (event) => {
    if (!event.target.matches("quick-buy-modal")) return;
    const state = states.get(event.target);
    state?.controller.abort();
    if (state) {
      if (state.originalLabel === null) event.target.removeAttribute("aria-label");
      else event.target.setAttribute("aria-label", state.originalLabel);
    }
    states.delete(event.target);
    event.target.querySelector("[data-quick-buy-status]")?.remove();
    event.target.removeAttribute("aria-busy");
    lock(event.target, false);
    event.target.querySelectorAll("variant-picker, quantity-selector").forEach((element) => { element.inert = false; });
  }, true);
}

install();